import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import type { DecoratedUnisonProject } from '@witmetrics/utils';
import { findById, mapToId } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { isBlank } from '@witmetrics/utils/strings';
import { useDispatch } from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { useString } from '@/hooks/useString';
import { useToggle } from '@/hooks/useToggle';
import Box from '@/components/Box';
import ProjectsList, { ProjectsListItem } from '@/components/ProjectsList';
import { CloseIcon, DescendingIcon } from '@/icons';
import { MAX_SIZE_MODIFIER } from './constants';
import { classes, fetchRecentProjects, styles } from './utils';

export type SearchableCaseDropdownProps = {
  disabled?: boolean;
  className?: string;
  label?: string;
  value: DecoratedUnisonProject | null;
  onSearch: (search: string) => Promise<DecoratedUnisonProject[]>;
  onSelect: (project: DecoratedUnisonProject | null) => void;
};

export default function SearchableCaseDropdown({
  disabled,
  className,
  label = 'Select a Case',
  value,
  onSearch,
  onSelect,
}: SearchableCaseDropdownProps) {
  const timestampRef = useRef(Date.now());
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { onApiError, recentProjectIds } = useAppState();
  const [search, setSearch] = useString();
  const [suggestions, setSuggestions] = useState<DecoratedUnisonProject[]>([]);
  const [recentProjects, setRecentProjects] = useState<
    DecoratedUnisonProject[]
  >([]);
  const [isSearching, toggleSearching] = useToggle();
  const [isOpen, toggleOpen] = useToggle();

  useEffect(() => {
    handleFetchRecentProjects();
  }, [recentProjectIds]);

  const handleFetchRecentProjects = async () => {
    if (isEmpty(recentProjectIds)) {
      setRecentProjects([]);
      return;
    }
    try {
      const projects = await fetchRecentProjects(recentProjectIds);
      dispatch.addProjects(projects);
      setRecentProjects(projects);
    } catch (err) {
      onApiError(err, 'Error fetching recent cases');
    }
  };

  const handleSearch = async (query: string) => {
    setSearch(query);
    toggleOpen(true);
    if (isBlank(query)) {
      setSuggestions([]);
      return;
    }
    const timestamp = Date.now();
    timestampRef.current = timestamp;
    toggleSearching(true);
    const projects = await onSearch(query);
    if (timestamp < timestampRef.current) return;
    dispatch.addProjects(projects);
    setSuggestions(projects);
    toggleSearching(false);
  };

  const handleSelectId = (projectId: number) => {
    const project = findById([...suggestions, ...recentProjects], projectId);
    if (!project) return;
    onSelect(project);
    toggleOpen(false);
    setSearch('');
    setSuggestions([]);
  };

  const handleClearSearch = () => {
    setSearch('');
    setSuggestions([]);
  };

  const renderPopperContent = () => {
    if (isSearching) {
      return (
        <ProjectsList
          isFetchingData
          showUser
          className={classes.list}
          onSelect={handleSelectId}
        />
      );
    }
    if (isBlank(search)) {
      if (isEmpty(recentProjects)) return null;
      return (
        <>
          <div className={classes.recentlyViewed}>Recently viewed</div>
          <ProjectsList
            showUser
            className={classes.list}
            projectIds={mapToId(recentProjects)}
            onSelect={handleSelectId}
          />
        </>
      );
    }
    if (isEmpty(suggestions)) {
      return <div className={classes.message}>No matching cases</div>;
    }
    return (
      <ProjectsList
        showUser
        className={classes.list}
        projectIds={mapToId(suggestions)}
        onSelect={handleSelectId}
      />
    );
  };

  if (value) {
    return (
      <Box
        sx={styles.selectedField}
        className={twMerge(classes.selectedField, className)}>
        <ul className={classes.selectedFieldList}>
          <ProjectsListItem
            active
            flat
            showUser
            projectId={value.id}
            onSelect={disabled ? undefined : () => onSelect(null)}
          />
        </ul>
      </Box>
    );
  }

  const popperContent = renderPopperContent();

  return (
    <ClickAwayListener onClickAway={() => toggleOpen(false)}>
      <div ref={wrapperRef} className={twMerge(classes.wrapper, className)}>
        <TextField
          fullWidth
          disabled={disabled}
          className={classes.textField}
          autoComplete="off"
          label={label}
          value={search}
          InputProps={{
            endAdornment: (
              <InputAdornment sx={styles.endAdornment} position="end">
                {!isBlank(search) && !isSearching && (
                  <IconButton
                    sx={styles.clearButton}
                    size="small"
                    tabIndex={-1}
                    onClick={handleClearSearch}>
                    <CloseIcon className={classes.caret} fontSize="small" />
                  </IconButton>
                )}
                {isSearching ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    sx={styles.popupButton}
                    size="small"
                    onClick={() => toggleOpen(true)}>
                    <DescendingIcon className={classes.caret} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => toggleOpen(true)}
        />
        <Popper
          open={isOpen && !!popperContent}
          style={styles.popper(wrapperRef.current?.clientWidth)}
          anchorEl={wrapperRef.current}
          placement="bottom-start"
          modifiers={[{ name: 'flip', enabled: false }, MAX_SIZE_MODIFIER]}>
          <Paper sx={styles.paper} className={classes.paper}>
            {popperContent}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
